import React, { useMemo, useState } from 'react';
import { IoSwapVerticalOutline } from 'react-icons/io5';
import './PresaleCalculator.css';

const QUICK_USDT = [100, 500, 1000, 5000];

const fmt = (n, digits) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '';
  return v.toLocaleString(undefined, { maximumFractionDigits: digits });
};

// USDT -> token estimator for the presale. Purely informational: the buyer
// still sends USDT to the deposit address below, so this never submits a tx.
const PresaleCalculator = ({
  tokenPrice,
  tokenSymbol = 'VIIT',
  minPurchase,
  maxPurchase,
}) => {
  const price = Number(tokenPrice);
  const min = minPurchase != null ? Number(minPurchase) : null;
  const max = maxPurchase != null ? Number(maxPurchase) : null;

  // Track raw input strings so the field the user is typing in stays untouched.
  const [usdt, setUsdt] = useState('');
  const [viit, setViit] = useState('');

  const onUsdt = (raw) => {
    const clean = raw.replace(/[^0-9.]/g, '');
    setUsdt(clean);
    const n = parseFloat(clean);
    setViit(Number.isFinite(n) && price > 0 ? String(n / price) : '');
  };

  const onViit = (raw) => {
    const clean = raw.replace(/[^0-9.]/g, '');
    setViit(clean);
    const n = parseFloat(clean);
    setUsdt(Number.isFinite(n) && price > 0 ? String(n * price) : '');
  };

  const usdtNum = parseFloat(usdt);
  const hasAmount = Number.isFinite(usdtNum) && usdtNum > 0;

  const warning = useMemo(() => {
    if (!hasAmount) return null;
    if (min != null && min > 0 && usdtNum < min) return `Minimum is ${fmt(min, 2)} USDT`;
    if (max != null && max > 0 && usdtNum > max) return `Maximum is ${fmt(max, 2)} USDT`;
    return null;
  }, [hasAmount, usdtNum, min, max]);

  if (!(price > 0)) return null;

  return (
    <div className="pcalc">
      <div className="pcalc-head">
        <span className="pcalc-title">Estimate your tokens</span>
        <span className="pcalc-rate">1 {tokenSymbol} = ${fmt(price, 8) || price}</span>
      </div>

      <div className="pcalc-field">
        <label htmlFor="pcalc-usdt">You send</label>
        <div className={`pcalc-input-wrap ${warning ? 'pcalc-invalid' : ''}`}>
          <input
            id="pcalc-usdt"
            inputMode="decimal"
            placeholder="0.00"
            value={fmt(usdt, 6) || usdt}
            onChange={(e) => onUsdt(e.target.value)}
          />
          <span className="pcalc-unit">USDT</span>
        </div>
      </div>

      <div className="pcalc-swap" aria-hidden="true">
        <IoSwapVerticalOutline />
      </div>

      <div className="pcalc-field">
        <label htmlFor="pcalc-viit">You receive (estimated)</label>
        <div className="pcalc-input-wrap pcalc-output">
          <input
            id="pcalc-viit"
            inputMode="decimal"
            placeholder="0.00"
            value={fmt(viit, 4) || viit}
            onChange={(e) => onViit(e.target.value)}
          />
          <span className="pcalc-unit">{tokenSymbol}</span>
        </div>
      </div>

      <div className="pcalc-chips">
        {QUICK_USDT.map((amt) => (
          <button key={amt} type="button" className="pcalc-chip" onClick={() => onUsdt(String(amt))}>
            ${amt.toLocaleString()}
          </button>
        ))}
      </div>

      {warning ? (
        <div className="pcalc-warning">{warning}</div>
      ) : (min != null && min > 0) || (max != null && max > 0) ? (
        <div className="pcalc-hint">
          {min != null && min > 0 ? `Min ${fmt(min, 2)}` : null}
          {min != null && min > 0 && max != null && max > 0 ? ' · ' : null}
          {max != null && max > 0 ? `Max ${fmt(max, 2)}` : null}
          {' USDT'}
        </div>
      ) : null}

      <div className="pcalc-note">Final amount is confirmed on-chain when your deposit is received.</div>
    </div>
  );
};

export default PresaleCalculator;
